import type { MinMaxData } from '@/lib/graph-filter-utils-types.ts'
import { FILTER_KEYS } from '@/services/filter-layer-service/constants'
import { feetToMeters } from '@/components/shared/x-distance.vue'
import { error } from '@/lib/logger'

// This is a special case where the RC difficulty attribute keys
// diverge between offroading, hiking, and biking trails
export const hikeDifficulty = 'hikeDifficulty'
export const bikeDifficulty = 'bikeDifficulty'

// Map filter keys to their GQL filter name
const filterMappings: Record<string, string> = {
  [FILTER_KEYS.LENGTH]: 'lengthInMeters',
  [FILTER_KEYS.TECH_RATING]: 'technicalRating',
  [FILTER_KEYS.TECH_RATING_AS_DIFFICULTY]: 'technicalRating',
  [FILTER_KEYS.MOTOR_HOURS]: 'motorHours',
  [FILTER_KEYS.DIFFICULTY]: 'difficultySymbol',
  [FILTER_KEYS.HIKE_HOURS]: 'hikeHours',
  [FILTER_KEYS.ELEV_LOSS]: 'elevationLoss',
  [FILTER_KEYS.ELEV_GAIN]: 'elevationGain',
  [FILTER_KEYS.ROUTE_TYPE]: 'routeType',
  [FILTER_KEYS.ELEV_MIN]: 'elevationMin',
  [FILTER_KEYS.ELEV_MAX]: 'elevationMax',
  hikeDifficulty: 'hikeDifficultyDisplay',
  bikeDifficulty: 'difficultyRating',
  [FILTER_KEYS.PETS]: 'pets',
  [FILTER_KEYS.E_BIKES]: 'eBikes',
  [FILTER_KEYS.COLOR_RATING]: 'colorRating',
}

/**
 * GQL formatting requires values to have the single quotes replaced with double quotes
 * @param value
 */
export const formatDataQuotes = (value: string | string[]): string =>
  JSON.stringify(value).replace(/'/g, '\\"')

/**
 * Convert data appropriately for filter
 */
export function convertMinMaxData(
  data: MinMaxData,
  isImperial: boolean
): MinMaxData {
  return isImperial
    ? {
        min: feetToMeters(data.min),
        max: feetToMeters(data.max),
      }
    : {
        min: data.min,
        max: data.max,
      }
}

/**
 * Format for Greater Than
 * @param filter - Name of filter, from FILTER_KEYS
 * @param value - Filter value
 * @returns - String formatted to add to graphQL query
 */
export function gqlAttributeFilterGT(filter: string, value: number): string {
  const name = filterMappings[filter]
  return `{ name: "${name}", op: GT, value: ${value}}`
}

/**
 * Format for Greater Than or Equals
 * @param filter - Name of filter, from FILTER_KEYS
 * @param value - Filter value
 * @returns - String formatted to add to graphQL query
 */
export function gqlAttributeFilterGTE(filter: string, value: number): string {
  const name = filterMappings[filter]
  return `{ name: "${name}", op: GTE, value: ${value}}`
}

/**
 * Format for Less Than
 * @param filter Name of filter, from FILTER_KEYS
 * @param value Filter value
 * @returns String formatted to add to graphQL query
 */
export function gqlAttributeFilterLT(filter: string, value: number): string {
  const name = filterMappings[filter]
  return `{ name: "${name}", op: LT, value: ${value}}`
}

/**
 * Format for Less Than or Equals
 * @param filter - Name of filter, from FILTER_KEYS
 * @param value - Filter value
 * @returns String formatted to add to graphQL query
 */
export function gqlAttributeFilterLTE(filter: string, value: number) {
  const name = filterMappings[filter]
  return `{ name: "${name}", op: LTE, value: ${value}}`
}

/**
 * Format appropriate GQL filter expression to use with client slider-based filters
 * @param filter - Name of filter, from FILTER_KEYS
 * @param value - Array of currently selected difficulties
 * @returns String formatted to add to graphQL query
 */
export function gqlFilterInList(filter: string, value: string[]): string {
  const name = filterMappings[filter]
  // Replace single quotes with double quotes for GQL formatting
  const formattedData = formatDataQuotes(value)
  return `{ name: "${name}", op: IN, value: ${formattedData}}`
}

/**
 * Create filter for field greater than a number
 * @param field - Field the filter is based on
 * @param value - Number to be greater than
 * @returns String formatted to add to graphQL query
 */
export function gqlFilterGT(field: string, value: number) {
  return `${field}: { gt: ${value} }`
}

/**
 * Create filter for field less than a number
 * @param field - Field the filter is based on
 * @param value - Number to be less than
 * @returns String formatted to add to graphQL query
 */
export function gqlFilterLT(field: string, value: number) {
  return `${field}: { lt: ${value} }`
}

/**
 * Create filter for field between two numbers
 * @param field - Field the filter is based on
 * @param min - Number to be greater than
 * @param max - Number to be less than
 * @returns String formatted to add to graphQL query
 */
export function gqlFilterBetween(field: string, min: number, max: number) {
  return `${field}: { between: { min: ${min}, max: ${max} } }`
}

/**
 * Create filter for field equaling a given slug
 * @param field - Field the filter is based on
 * @param slug - Slug your field should equal
 * @returns String formatted to add to graphQL query
 */
export const gqlFilterSlugEquals = (field: string, slug: string) => {
  const formattedStr = formatDataQuotes(slug)
  return `${field}: { slug: { eq: ${formattedStr} } }`
}

/**
 * Create filter for field not equaling a given slug
 * @param field - Field the filter is based on
 * @param slug - Slug your field should not equal
 * @returns String formatted to add to graphQL query
 */
export const gqlFilterSlugNotEquals = (field: string, slug: string) => {
  const formattedStr = formatDataQuotes(slug)
  return `${field}: { slug: { neq: ${formattedStr} } }`
}

/**
 * Create filter for value being within a list of slugs
 * @param field - Field the filter is based on
 * @param slugs - Slugs your field should include one of
 * @returns String formatted to add to graphQL query
 */
export const gqlFilterSlugInList = (field: string, slugs: string[]) => {
  const formattedLaunches = formatDataQuotes(slugs)
  return `${field}: { slug: { in: ${formattedLaunches} } }`
}

/**
 * Create filter for chaining multiple expressions in an `and` filter
 * @param expressions - Expressions to join
 * @returns String formatted to add to graphQL query
 */
export const gqlFiltersAnd = (...expressions: string[]) => {
  // Check if all expressions are blank
  const allBlank = expressions.every((expression) => expression === '')

  if (allBlank) {
    return ''
  }
  return expressions.length ? `and: ${gqlArrayWrapper(expressions)}` : ''
}

/**
 * Create filter for chaining multiple expressions in an `or` filter
 * @param expressions - Expressions to join
 * @returns String formatted to add to graphQL query
 */
export const gqlFiltersOr = (...expressions: string[]) => {
  return `or: ${gqlArrayWrapper(expressions)}`
}

/**
 * Wrap multiple expressions in appropriate array brackets for queries
 * @param expressions - Expressions to join
 * @returns String formatted to add to graphQL query
 */
export const gqlArrayWrapper = (expressions: string[]) => {
  let formattedExpression = ''
  expressions.forEach((expression: string, index: number) => {
    if (expression === '') {
      return
    }
    if (index !== expressions.length - 1) {
      formattedExpression += `${expression}, `
    } else {
      formattedExpression += expression
    }
  })
  return `[${formattedExpression}]`
}

/**
 * Wrap an expression in appropriate braces for queries
 * @param expression - Expression to wrap
 * @param trailingComma - Include trailing comma or not
 * @returns String formatted to add to graphQL query
 */
export const gqlBraceWrapper = (
  expression: string,
  trailingComma = false
): string => {
  if (!expression) {
    return ''
  }
  return trailingComma ? `{ ${expression} }, ` : `{ ${expression} }`
}

/**
 * Convert a raw gql filter expression string to a JSON object
 */
export const gqlFilterExpressionToJson = (expression: string) => {
  // Add braces to raw filter expression string
  let jsonString = gqlBraceWrapper(expression)

  // wrap keys with double quotes
  jsonString = jsonString.replace(/([a-zA-Z0-9_]+):/g, '"$1":')

  // Parse the string into JSON
  try {
    const jsonObj = JSON.parse(jsonString)
    return jsonObj
  } catch (e) {
    error(
      'Logger',
      'Invalid string format to parse to JSON for string:',
      expression,
      e
    )
    return {}
  }
}
